import { useCallback, useEffect } from 'react';

import { useTracking } from '@skyscanner/strevda-tracking';
import { useAdvertVisibilityTracking } from '@web-platform/shared-banana-redux/src/helpers/hooks/useAdvertVisibilityTracking';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { getStrevdaTrackingConfig } from '@web-platform/shared-universal-tracking/src/getStrevdaTrackingConfig';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import { useInViewportOnce } from '@web-platform/shared-universal-utils-frontend/src/helpers/hooks/useInViewportOnce';
import {
  logWarn,
  logOperationalEvent,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { redirect } from 'saddlebag-browser';

import { HomepageVideo } from './HomepageVideo';

import type { HomepageVideo as HomepageVideoAdvert } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/homepageVideo';

export type Props = {
  advert: HomepageVideoAdvert;
};
export const HomepageVideoCard = ({ advert }: Props) => {
  const { analyticsProperties, creative } = advert;
  const { ref: trackingRef, trackClick } = useTracking(
    advert,
    {},
    false,
    getStrevdaTrackingConfig(),
    { viewabilityDwellMs: 2000 },
  );
  const { ref: visibilityTrackerRef } = useAdvertVisibilityTracking();
  const onVisible = useCallback(() => {
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_VIEWED.NAME,
      strings: {
        creativeId: analyticsProperties.idCreative,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_VIEWED.NAME,
      lineItemId: analyticsProperties.idCreative,
    });
  }, [analyticsProperties.idCreative]);
  const { ref: onVisibleRef } = useInViewportOnce(onVisible);
  const combinedRef = useCallback(
    (el: HTMLDivElement) => {
      visibilityTrackerRef.current = el;
      trackingRef(el);
      onVisibleRef.current = el;
    },
    [trackingRef, visibilityTrackerRef, onVisibleRef],
  );

  useEffect(() => {
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_LOADED.NAME,
      strings: {
        creativeId: analyticsProperties.idCreative,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_LOADED.NAME,
      lineItemId: analyticsProperties.idCreative,
    });
  }, [analyticsProperties.idCreative]);

  const homepageVideoOnClick = useCallback(
    (mediaType: 'image' | 'video') => {
      trackClick(mediaType);
      trackInteractionEvent({
        name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_CTA_TAPPED.NAME,
        strings: {
          creativeId: analyticsProperties.idCreative,
          mediaType,
        },
      });
      logOperationalEvent({
        eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_CTA_TAPPED.NAME,
        lineItemId: analyticsProperties.idCreative,
        mediaType,
      });
      redirect(creative.ctaUrl);
    },
    [analyticsProperties.idCreative, creative.ctaUrl, trackClick],
  );

  if (!creative.partnerName) {
    logWarn({
      component: 'HomepageVideo',
      message: 'Merchandising Homepage Videos are not yet supported.',
      data: JSON.stringify(creative),
    });
    return null;
  }

  const creativeId = analyticsProperties.idCreative;
  // Unset decisions return undefined, so a failed lookup degrades to the legacy player.
  const useSpicyVideoPlayer = !!getFeatureDecision(
    FEATURE_DECISION_KEYS.ENABLE_SPICY_HOMEPAGE_VIDEO_PLAYER,
  );

  return (
    <div ref={combinedRef}>
      {/* key forces a fresh mount per creative so readiness/mute/play state can't leak */}
      <HomepageVideo
        key={creativeId}
        advert={advert}
        creativeId={creativeId}
        onClick={homepageVideoOnClick}
        useSpicyVideoPlayer={useSpicyVideoPlayer}
      />
    </div>
  );
};
